import { useEffect, useMemo, useState } from "react";
import { fetchSymbols, updateSettings } from "@/api/client";
import type { TradableSymbol } from "@/api/types";

interface SymbolManagementPanelProps {
  open: boolean;
  onClose: () => void;
}

export default function SymbolManagementPanel({ open, onClose }: SymbolManagementPanelProps) {
  const [available, setAvailable] = useState<TradableSymbol[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [initialSelected, setInitialSelected] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    setSavedAt(null);
    fetchSymbols()
      .then((res) => {
        setAvailable(res.available);
        const enabled = new Set(res.enabled);
        setSelected(enabled);
        setInitialSelected(enabled);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load symbols"))
      .finally(() => setLoading(false));
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toUpperCase();
    if (!q) return available;
    return available.filter(
      (s) => s.symbol.includes(q) || s.baseAsset.includes(q),
    );
  }, [available, query]);

  const dirty = useMemo(() => {
    if (selected.size !== initialSelected.size) return true;
    for (const s of selected) if (!initialSelected.has(s)) return true;
    return false;
  }, [selected, initialSelected]);

  function toggle(symbol: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(symbol)) next.delete(symbol);
      else next.add(symbol);
      return next;
    });
  }

  function selectAllShown() {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const s of filtered) next.add(s.symbol);
      return next;
    });
  }

  function deselectAllShown() {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const s of filtered) next.delete(s.symbol);
      return next;
    });
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      const res = await updateSettings({ enabledSymbols: Array.from(selected) });
      const enabled = new Set(
        Array.isArray(res.settings.enabledSymbols) ? res.settings.enabledSymbols : [],
      );
      setSelected(enabled);
      setInitialSelected(enabled);
      setSavedAt(Date.now());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save symbol selection");
    } finally {
      setSaving(false);
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-void/80 px-4 py-10 backdrop-blur-sm">
      <div className="w-full max-w-2xl rounded-lg border border-line bg-panel shadow-panel">
        <div className="flex items-center justify-between border-b border-line px-5 py-4">
          <div className="font-mono text-sm font-semibold uppercase tracking-widest text-ink">
            Manual Symbols
          </div>
          <button
            onClick={onClose}
            className="rounded-md border border-line px-2.5 py-1 font-mono text-xs text-ink-dim transition hover:border-sell/50 hover:text-sell"
          >
            Close
          </button>
        </div>

        <div className="space-y-3 border-b border-line px-5 py-3">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search symbols (e.g. SOL, BTCUSDT)…"
            className="w-full rounded-md border border-line bg-raised px-3 py-1.5 font-mono text-xs text-ink outline-none focus:border-turquoise/60"
          />
          <div className="flex items-center justify-between font-mono text-[11px] text-ink-faint">
            <span>
              {selected.size} manually added
              {selected.size === 0 ? " — scanning automatically selected symbols only" : ""}
              {query ? ` · ${filtered.length} shown` : ""}
            </span>
            <div className="flex gap-3">
              <button
                onClick={selectAllShown}
                disabled={filtered.length === 0}
                className="text-turquoise hover:underline disabled:cursor-not-allowed disabled:opacity-40"
              >
                Select all{query ? " shown" : ""}
              </button>
              <button
                onClick={deselectAllShown}
                disabled={filtered.length === 0}
                className="text-ink-dim hover:underline disabled:cursor-not-allowed disabled:opacity-40"
              >
                Deselect all{query ? " shown" : ""}
              </button>
            </div>
          </div>
        </div>

        <div className="max-h-[50vh] overflow-y-auto px-5 py-4">
          {loading && <div className="font-mono text-xs text-ink-faint">Loading tradable symbols…</div>}
          {error && (
            <div className="mb-3 rounded-md border border-sell/40 bg-sell/10 px-3 py-2 font-mono text-xs text-sell">
              {error}
            </div>
          )}
          {!loading && !error && filtered.length === 0 && (
            <div className="font-mono text-xs text-ink-faint">No symbols match "{query}".</div>
          )}
          {!loading && filtered.length > 0 && (
            <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
              {filtered.map((s) => {
                const isSelected = selected.has(s.symbol);
                return (
                  <button
                    key={s.symbol}
                    onClick={() => toggle(s.symbol)}
                    className={`flex items-center justify-between rounded-md border px-2.5 py-1.5 font-mono text-xs transition ${
                      isSelected
                        ? "border-turquoise/50 bg-turquoise/10 text-turquoise"
                        : "border-line bg-raised text-ink-dim hover:border-line-soft"
                    }`}
                  >
                    <span>{s.symbol}</span>
                    {isSelected && <span className="ml-1.5">✓</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-line px-5 py-4">
          <div className="font-mono text-[11px] text-ink-faint">
            {savedAt && !dirty ? "Saved." : dirty ? "Unsaved changes" : "No changes"}
          </div>
          <button
            onClick={handleSave}
            disabled={saving || !dirty}
            className="rounded-md border border-turquoise/50 bg-turquoise/10 px-4 py-1.5 font-mono text-xs font-semibold uppercase tracking-wider text-turquoise transition hover:bg-turquoise/20 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {saving ? "Saving…" : "Save selection"}
          </button>
        </div>
      </div>
    </div>
  );
}
